import { GridPaginationModel } from "@mui/x-data-grid";
import React from "react";

import { formatValue } from "../lib/format";
import { Field, fieldLabel, mobileCardFields } from "../lib/schema-field";
import { rowKeyOrHash } from "../lib/row-key";
import { Button } from "./ui/Button";

type MobileRecordListProps = {
  rows: any[];
  schema: Field[];
  idField?: string;
  loading?: boolean;
  onOpen: (row: any) => void;
};

const cardClass =
  "flex min-h-11 w-full flex-col gap-1 rounded-lg border border-od-border bg-od-surface px-3 py-2.5 text-left transition-colors hover:bg-od-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

export const MobileRecordList: React.FC<MobileRecordListProps> = ({
  rows,
  schema,
  idField,
  loading,
  onOpen,
}) => {
  const { title, extras } = mobileCardFields(schema);

  if (loading && rows.length === 0) {
    return (
      <p className="px-3 py-6 text-sm text-od-muted" role="status">
        Loading…
      </p>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-3">
      {rows.map((row) => {
        const heading = title
          ? formatValue(row?.[title.name], title)
          : "Record";
        return (
          <li key={rowKeyOrHash(row, idField)}>
            <button
              type="button"
              onClick={() => onOpen(row)}
              className={cardClass}
            >
              <span className="truncate text-sm font-medium text-od-text">
                {heading || "Untitled"}
              </span>
              {extras.map((field) => (
                <span
                  key={field.name}
                  className="flex min-w-0 justify-between gap-3 text-xs text-od-muted"
                >
                  <span className="shrink-0">{fieldLabel(field)}</span>
                  <span className="min-w-0 truncate text-od-text">
                    {formatValue(row?.[field.name], field)}
                  </span>
                </span>
              ))}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

type MobilePageBarProps = {
  page: number;
  pageSize: number;
  total: number;
  onChange: (model: GridPaginationModel) => void;
};

export const MobilePageBar: React.FC<MobilePageBarProps> = ({
  page,
  pageSize,
  total,
  onChange,
}) => {
  if (total <= 0) return null;
  const last = Math.max(0, Math.ceil(total / pageSize) - 1);
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
    return (
    <div className="flex items-center justify-between gap-2 border-t border-od-border px-3 py-2">
      <Button
        variant="secondary"
        className="min-w-11 px-3 text-sm"
        disabled={page <= 0}
        aria-label="Previous page"
        onClick={() => onChange({ page: page - 1, pageSize })}
      >
        Prev
      </Button>
      <span className="text-xs tabular-nums text-od-muted">
        {from}–{to} of {total}
      </span>
      <Button
        variant="secondary"
        className="min-w-11 px-3 text-sm"
        disabled={page >= last}
        aria-label="Next page"
        onClick={() => onChange({ page: page + 1, pageSize })}
      >
        Next
      </Button>
    </div>
  );
};
